import React, { useEffect, useState } from "react";
import { Button, InputNumber, Select, message } from "antd";
import { AN_COLORS } from "../../design-system/tokens";
import { fmtDate } from "../../design-system/tableUi";
import { isAdminUser } from "../../utils/helpers";
import { listIamUsers } from "../../service/appPolicyService";
import {
  getNoPolicySetting,
  getPasswordRotationSetting,
  setNoPolicySetting,
  setPasswordRotationSetting,
} from "../../service/dbVaultService";
import {
  ROTATION_DEFAULT_HOURS,
  ROTATION_MAX_HOURS,
  ROTATION_MIN_HOURS,
  ROTATION_PRESETS,
  hoursHint,
  lastChanged,
  rotationHoursError,
  userLabel,
} from "./dbVaultModel";

// Database → Settings: tenant-wide database settings on the Databases page.
//   Logins no policy covers   settings/noPolicy        (allow | mfa | deny)
//   Password rotation         settings/passwordRotation (hours, 1–8760)
// Both are stored in one row, so updatedAt / updatedBy are shown once as the
// section's "Last changed".

const NO_POLICY_OPTIONS = [
  { value: "allow", label: "Allow" },
  { value: "mfa", label: "Require MFA" },
  { value: "deny", label: "Deny" },
];

const Row = ({ label, help, children, last }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(16rem, 1fr))",
      gap: "1rem 1.5rem",
      alignItems: "start",
      padding: "1rem 1.25rem",
      borderBottom: last ? "none" : `1px solid ${AN_COLORS.line2}`,
    }}
  >
    <div>
      <div style={{ color: AN_COLORS.ink, fontWeight: 500, fontSize: "0.875rem" }}>{label}</div>
      {help && <div style={{ color: AN_COLORS.muted, fontSize: "0.8125rem", marginTop: "0.25rem", lineHeight: 1.5 }}>{help}</div>}
    </div>
    <div>{children}</div>
  </div>
);

const DatabaseSettingsCard = () => {
  const canEdit = isAdminUser();
  const [notification, contextHolder] = message.useMessage();

  const [noPolicy, setNoPolicy] = useState(null); // { noPolicyAction, updatedAt, updatedBy }
  const [rotation, setRotation] = useState(null); // { passwordRotationHours, updatedAt, updatedBy }
  const [hours, setHours] = useState(ROTATION_DEFAULT_HOURS);
  const [loadFailed, setLoadFailed] = useState(false);
  const [savingNoPolicy, setSavingNoPolicy] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let live = true;
    getNoPolicySetting()
      .then((res) => live && setNoPolicy(res || {}))
      .catch(() => live && setLoadFailed(true));
    getPasswordRotationSetting()
      .then((res) => {
        if (!live) return;
        setRotation(res || {});
        const h = Number(res?.passwordRotationHours);
        setHours(Number.isInteger(h) && h > 0 ? h : ROTATION_DEFAULT_HOURS);
      })
      .catch(() => live && setLoadFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const changed = lastChanged(noPolicy, rotation);

  // Resolve "updatedBy" to a name once there is one to resolve.
  useEffect(() => {
    if (changed?.by == null || users.length) return;
    listIamUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [changed?.by, users.length]);

  const saveNoPolicy = (value) => {
    const prev = noPolicy;
    setNoPolicy((s) => ({ ...(s || {}), noPolicyAction: value }));
    setSavingNoPolicy(true);
    setNoPolicySetting(value)
      .then((res) => {
        setNoPolicy(res || { noPolicyAction: value });
        notification.success({ content: "Saved." });
      })
      .catch((err) => {
        setNoPolicy(prev);
        notification.error({
          content: err?.response?.status === 403 ? "Only administrators can change this." : "Couldn't save the setting.",
        });
      })
      .finally(() => setSavingNoPolicy(false));
  };

  const storedHours = Number(rotation?.passwordRotationHours) || ROTATION_DEFAULT_HOURS;
  const hoursError = rotationHoursError(hours);
  const hoursDirty = Number(hours) !== storedHours;

  const saveHours = () => {
    if (hoursError) return;
    setSavingHours(true);
    setPasswordRotationSetting(Number(hours))
      .then((res) => {
        setRotation(res || { passwordRotationHours: Number(hours) });
        notification.success({ content: "Saved. Managed passwords use the new interval from their next rotation." });
      })
      .catch((err) => {
        const status = err?.response?.status;
        notification.error({
          content:
            status === 400
              ? `Use a whole number of hours from ${ROTATION_MIN_HOURS} to ${ROTATION_MAX_HOURS}.`
              : status === 403
                ? "Only administrators can change this."
                : "Couldn't save the setting.",
        });
      })
      .finally(() => setSavingHours(false));
  };

  const hint = hoursHint(hours);

  return (
    <section
      aria-labelledby="db-settings-title"
      style={{
        marginTop: "1.25rem",
        background: AN_COLORS.surface,
        border: `1px solid ${AN_COLORS.line}`,
        borderRadius: "0.625rem",
      }}
    >
      {contextHolder}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap",
          padding: "0.875rem 1.25rem",
          borderBottom: `1px solid ${AN_COLORS.line}`,
        }}
      >
        <h2 id="db-settings-title" style={{ margin: 0, fontSize: "0.9375rem", fontWeight: 600, color: AN_COLORS.ink }}>
          Settings
        </h2>
        {changed && (
          <span style={{ color: AN_COLORS.muted, fontSize: "0.75rem" }}>
            Last changed {fmtDate(changed.at)}
            {changed.by != null ? ` by ${userLabel(changed.by, users)}` : ""}
          </span>
        )}
      </div>

      {loadFailed && (
        <div style={{ padding: "0.75rem 1.25rem 0", color: AN_COLORS.danger, fontSize: "0.8125rem" }}>
          Couldn't load the current settings.
        </div>
      )}

      <Row
        label="Logins no policy covers"
        help="What a database login gets when no database policy matches it."
      >
        <Select
          style={{ width: "100%", maxWidth: "16rem" }}
          value={noPolicy?.noPolicyAction || undefined}
          placeholder={noPolicy ? "Allow" : "Loading…"}
          options={NO_POLICY_OPTIONS}
          loading={savingNoPolicy}
          disabled={!canEdit || !noPolicy || savingNoPolicy}
          onChange={saveNoPolicy}
        />
      </Row>

      <Row
        label="Rotate managed database passwords every"
        help="The default for every password AuthNull manages in this tenant."
        last
      >
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
          <InputNumber
            aria-label="Rotation interval in hours"
            min={ROTATION_MIN_HOURS}
            max={ROTATION_MAX_HOURS}
            step={1}
            precision={0}
            value={hours}
            onChange={(v) => setHours(v)}
            disabled={!canEdit || !rotation}
            addonAfter="hours"
            style={{ width: "10rem" }}
          />
          <Button type="primary" onClick={saveHours} loading={savingHours} disabled={!canEdit || !rotation || !hoursDirty || !!hoursError}>
            Save
          </Button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem", marginTop: "0.5rem" }}>
          {ROTATION_PRESETS.map((p) => (
            <Button
              key={p.hours}
              size="small"
              type={Number(hours) === p.hours ? "primary" : "default"}
              ghost={Number(hours) === p.hours}
              disabled={!canEdit || !rotation}
              onClick={() => setHours(p.hours)}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div style={{ marginTop: "0.375rem", fontSize: "0.75rem", color: hoursError ? AN_COLORS.danger : AN_COLORS.muted }}>
          {hoursError || (hint ? `That's ${hint}.` : " ")}
        </div>
      </Row>
    </section>
  );
};

export default DatabaseSettingsCard;
